/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */
import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePageTranslationScanner } from '../hooks/usePageTranslationScanner';
import {
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Box,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Tab,
  Tabs,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Forum as ForumIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useDebate } from '../contexts/DebateContext';
import { useAuth } from '../contexts/AuthContext';
import { Debate } from '../types';
import EmailVerificationBanner from '../components/EmailVerificationBanner';
import EmailVerificationGuard from '../components/EmailVerificationGuard';
import logger from '../utils/logger';

const UserDebatesPage: React.FC = () => {
  const { t } = useTranslation();
  
  // Enable translation scanning for this critical page
  usePageTranslationScanner(false);
  
  const { 
    activeDebates, 
    completedDebates,
    debouncedFetchActiveDebates, 
    debouncedFetchCompletedDebates,
    loading, 
    error 
  } = useDebate();
  
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  
  // For now, let's consider debates as private/user debates if the user is a participant
  // This is a simplified approach until we implement proper debate ownership
  const [userActiveDebates, setUserActiveDebates] = useState<Debate[]>([]);
  const [userCompletedDebates, setUserCompletedDebates] = useState<Debate[]>([]);
  
  // Use a ref to track whether we've already done the initial data fetch
  const initialFetchDoneRef = React.useRef(false);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredActive, setFilteredActive] = useState<Debate[]>([]);
  const [filteredCompleted, setFilteredCompleted] = useState<Debate[]>([]);
  
  // Fetch debates on component mount - only once
  useEffect(() => {
    // Only fetch if we haven't already done the initial fetch
    if (!initialFetchDoneRef.current) {
      logger.info('UserDebatesPage: Performing initial debates fetch');
      debouncedFetchActiveDebates();
      debouncedFetchCompletedDebates();
      initialFetchDoneRef.current = true; // Mark initial fetch as done
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array with ESLint exception to prevent infinite loop
  
  // Helper function to check if it's the user's turn in a debate
  const isUserTurn = (debate: Debate, userId: string) => {
    if (!debate.currentTurn) return false;
    return debate.currentTurn.userIds?.some((id: string) => String(id) === String(userId));
  };

  // Helper function to get the last message timestamp
  const getLastMessageTimestamp = (debate: Debate) => {
    if (!debate.messages || debate.messages.length === 0) {
      return new Date(debate.createdAt).getTime();
    }
    const lastMessage = debate.messages[debate.messages.length - 1];
    const messageTime = lastMessage.timestamp || lastMessage.createdAt || debate.createdAt;
    return new Date(messageTime).getTime();
  };

  // Helper function to check if a debate is stale (24 hours before scheduled completion)
  const isDebateStale = (debate: Debate) => {
    if (!debate.scheduledCompletionTime) {
      // Fallback to old logic if scheduledCompletionTime is not set
      const lastMessageTime = getLastMessageTimestamp(debate);
      const sixDaysAgo = Date.now() - (6 * 24 * 60 * 60 * 1000);
      return lastMessageTime < sixDaysAgo;
    }
    
    // Show red frame 24 hours before scheduled completion
    const scheduledTime = new Date(debate.scheduledCompletionTime).getTime();
    const oneDayBeforeCompletion = scheduledTime - (24 * 60 * 60 * 1000);
    return Date.now() > oneDayBeforeCompletion;
  };

  // Filter debates to show only those where the user is a participant
  useEffect(() => {
    if (activeDebates && user) {
      // Filter debates where user is a participant or creator
      const userDebates = activeDebates.filter(debate => 
        debate.teams.some(team => 
          // Check if user is team owner
          team.userId === user.id || 
          // Or check if user is in the members array
          (team.members && Array.isArray(team.members) && 
           team.members.some(memberId => String(memberId) === String(user.id)))
        ) || 
        debate.createdBy === user.id
      );
      
      // Sort debates: first by user's turn, then by last message timestamp
      const sortedDebates = userDebates.sort((a, b) => {
        // First sort by whether it's user's turn
        const isATurn = isUserTurn(a, user.id);
        const isBTurn = isUserTurn(b, user.id);
        if (isATurn !== isBTurn) {
          return isATurn ? -1 : 1;
        }
        
        // Then sort by last message timestamp (most recent first)
        return getLastMessageTimestamp(b) - getLastMessageTimestamp(a);
      });
      
      setUserActiveDebates(sortedDebates);
    } else if (activeDebates) {
      // If no user, show empty array
      setUserActiveDebates([]);
    }
    
    if (completedDebates && user) {
      // Filter debates where user is a participant or creator
      const userDebates = completedDebates.filter(debate => 
        debate.teams.some(team => 
          // Check if user is team owner
          team.userId === user.id || 
          // Or check if user is in the members array
          (team.members && Array.isArray(team.members) && 
           team.members.some(memberId => String(memberId) === String(user.id)))
        ) || 
        debate.createdBy === user.id
      );
      
      // Sort completed debates by completion date (most recent first)
      const sortedDebates = userDebates.sort((a, b) => {
        return new Date(b.endDate || b.updatedAt).getTime() - new Date(a.endDate || a.updatedAt).getTime();
      });
      
      setUserCompletedDebates(sortedDebates);
    } else if (completedDebates) {
      // If no user, show empty array
      setUserCompletedDebates([]);
    }
  }, [activeDebates, completedDebates, user]);
  
  // Filter debates based on search term
  useEffect(() => {
    if (userActiveDebates) {
      setFilteredActive(
        userActiveDebates.filter(debate => 
          debate.topic.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (debate.description && debate.description.toLowerCase().includes(searchTerm.toLowerCase()))
        )
      );
    }
    
    if (userCompletedDebates) {
      setFilteredCompleted(
        userCompletedDebates.filter(debate => 
          debate.topic.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (debate.description && debate.description.toLowerCase().includes(searchTerm.toLowerCase()))
        )
      );
    }
  }, [searchTerm, userActiveDebates, userCompletedDebates]);
  
  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  // Render a debate card
  const renderDebateCard = (debate: Debate) => {
    const isStale = debate.status === 'active' && isDebateStale(debate);
    const isMyTurn = user && debate.status === 'active' && isUserTurn(debate, user.id);

    return (
      <Card
        variant="outlined"
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
          },
          ...(isStale && {
            borderColor: 'error.main',
          }),
        }}
      >
        <CardContent sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h6" component="h2" gutterBottom noWrap>
              {debate.topic}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {isMyTurn && (
                <Chip
                  label={t('yourTurn')}
                  size="small"
                  color="secondary"
                  sx={{ fontWeight: 'bold' }}
                />
              )}
              <Chip
                label={debate.isPublic ? t('public', 'Public') : t('private', 'Private')}
                size="small"
                color={debate.isPublic ? 'info' : 'warning'}
                sx={{ fontWeight: 'bold' }}
              />
              <Chip
                label={
                  debate.status === 'active'
                    ? t('inProgressText', 'In Progress')
                    : debate.status === 'completed'
                    ? t('completedText', 'Completed')
                    : t('pendingText', 'Pending')
                }
                size="small"
                color={
                  debate.status === 'active'
                    ? 'success'
                    : debate.status === 'completed'
                    ? 'default'
                    : 'primary'
                }
                sx={{ fontWeight: 'bold' }}
              />
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('created')}: {formatDate(debate.createdAt)}
            {debate.status === 'completed' && debate.endDate && (
              <>
                {' • '}{t('completed')}: {formatDate(debate.endDate)}
              </>
            )}
            {isStale && (
              <Typography component="span" color="error.main" sx={{ ml: 1 }}>
                • {t('noActivityForOverSixDays')}
              </Typography>
            )}
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <Typography variant="body2" sx={{ mb: 2 }}>
            {debate.description || t('noDescriptionProvided')}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            {t('teams')}: {debate.teams.map((team) => team.name).join(' vs. ')}
          </Typography>
        </CardContent>

        <CardActions>
          <Button
            component={RouterLink}
            to={`/debate/${debate.id}`}
            size="small"
            color="primary"
            endIcon={<ForumIcon />}
          >
            {debate.status === 'active' ? t('continueDebate') : t('viewResults')}
          </Button>
        </CardActions>
      </Card>
    );
  };

  const emptyMessage = tabValue === 0 
    ? t('noActiveDebatesYet')
    : t('noCompletedDebatesYet');

  // Add a refresh function
  const refreshDebates = () => {
    logger.info('Manually refreshing debates...');
    debouncedFetchActiveDebates();
    debouncedFetchCompletedDebates();
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <EmailVerificationBanner />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1">
          {t('yourDebates')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <EmailVerificationGuard action="create a debate">
            <Button
              component={RouterLink}
              to="/debates/new"
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
            >
              {t('newDebate')}
            </Button>
          </EmailVerificationGuard>
          <Button
            variant="outlined"
            color="primary"
            onClick={refreshDebates}
            startIcon={<RefreshIcon />}
          >
            {t('refresh')}
          </Button>
        </Box>
      </Box>
      
      {/* Search Bar */}
      <Paper sx={{ p: 2, mb: 4 }}>
        <TextField
          fullWidth
          placeholder={t('searchDebates')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>
      
      {/* Tabs for Active vs Completed Debates */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          aria-label="debate status tabs"
        >
          <Tab label={t('activeDebates')} />
          <Tab label={t('completedDebates')} />
        </Tabs>
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : (tabValue === 0 ? filteredActive : filteredCompleted).length === 0 ? (
        <Paper elevation={0} variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary" paragraph>
            {emptyMessage}
          </Typography>
          {tabValue === 0 && (
            <EmailVerificationGuard action="create your first debate">
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate('/debates/new')}
              >
                {t('createYourFirstDebate')}
              </Button>
            </EmailVerificationGuard>
          )}
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {(tabValue === 0 ? filteredActive : filteredCompleted).map((debate) => (
            <Grid item xs={12} md={6} key={debate.id}>
              {renderDebateCard(debate)}
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default UserDebatesPage; 